import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["use", "children", "borderRadius", "borderStartStartRadius", "borderStartEndRadius", "borderEndStartRadius", "borderEndEndRadius"];
import { View } from '../../../components/View';
import { getBadgeStyles, CountStyles } from './Badge.helpers';
import { BadgeProvider, useBadgeContext } from './BadgeProvider';
import { deepMerge } from '../../utils/deepMerge/deepMerge';
import styled, { useTheme } from 'foundations-theming/utils';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { jsx as _jsx } from "react/jsx-runtime";
const Dot = styled.span.withConfig({
  displayName: "Badge__Dot",
  componentId: "ifnuaj-0"
})(({
  theme
}) => {
  return {
    display: 'inline-block',
    verticalAlign: 'middle',
    backgroundColor: theme.color['fill-accent-magenta-default'],
    borderInlineWidth: theme.borderWidth['100'],
    borderBlockWidth: theme.borderWidth['100'],
    borderInlineStyle: 'solid',
    borderBlockStyle: 'solid',
    borderInlineColor: theme.color['border-primary-on-fill'],
    borderBlockColor: theme.color['border-primary-on-fill'],
    // no tokens for the below properties
    borderRadius: '20px 6px 6px 20px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
    blockSize: '10px',
    inlineSize: '18px',
    textIndent: '-10000px'
  };
});
export const StyledSpan = styled.span.withConfig({
  displayName: "Badge__StyledSpan",
  componentId: "ifnuaj-1"
})(({
  $cssStyles
}) => {
  return Object.assign({}, $cssStyles);
});
export const StyledTextWrapper = styled.span.withConfig({
  displayName: "Badge__StyledTextWrapper",
  componentId: "ifnuaj-2"
})(({
  theme,
  $use,
  $isBadge
}) => {
  return Object.assign({
    fontFamily: theme.typography['heading-25'].fontFamily,
    fontWeight: theme.typography['heading-25'].fontWeight,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.color[$use === 'koala' ? 'text-core-default' : 'text-primary-on-fill-default']
  }, $isBadge && {
    textTransform: 'uppercase'
  }, {
    // no tokens for the below properties
    lineHeight: $use === 'count' ? '19px' : '20px',
    fontSize: $use === 'count' ? '11px' : '10px'
  });
});
export const Badge = _ref => {
  var _theme$borderRadius, _theme$borderRadius2, _theme$borderRadius3, _theme$borderRadius4, _badgeContext$badgeSt;
  let {
      use = 'free',
      children,
      borderRadius = '100',
      borderStartStartRadius,
      borderStartEndRadius,
      borderEndStartRadius,
      borderEndEndRadius
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const isBadge = !(use === 'count' || use === 'dot');
  const Rendered = use !== 'dot' ? View : Dot;
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const theme = useTheme();
  const badgeContext = useBadgeContext();
  const badgeStyleOverrides = deepMerge(Object.assign({}, use !== 'count' ?
  // Default to the overall borderRadius if individual values are undefined
  {
    borderStartStartRadius: (_theme$borderRadius = theme.borderRadius[borderStartStartRadius !== null && borderStartStartRadius !== void 0 ? borderStartStartRadius : borderRadius]) !== null && _theme$borderRadius !== void 0 ? _theme$borderRadius : 'inherit',
    borderStartEndRadius: (_theme$borderRadius2 = theme.borderRadius[borderStartEndRadius !== null && borderStartEndRadius !== void 0 ? borderStartEndRadius : borderRadius]) !== null && _theme$borderRadius2 !== void 0 ? _theme$borderRadius2 : 'inherit',
    borderEndStartRadius: (_theme$borderRadius3 = theme.borderRadius[borderEndStartRadius !== null && borderEndStartRadius !== void 0 ? borderEndStartRadius : borderRadius]) !== null && _theme$borderRadius3 !== void 0 ? _theme$borderRadius3 : 'inherit',
    borderEndEndRadius: (_theme$borderRadius4 = theme.borderRadius[borderEndEndRadius !== null && borderEndEndRadius !== void 0 ? borderEndEndRadius : borderRadius]) !== null && _theme$borderRadius4 !== void 0 ? _theme$borderRadius4 : 'inherit'
  } : {}), (_badgeContext$badgeSt = badgeContext === null || badgeContext === void 0 ? void 0 : badgeContext.badgeStyleOverrides) !== null && _badgeContext$badgeSt !== void 0 ? _badgeContext$badgeSt : {});
  return /*#__PURE__*/_jsx(BadgeProvider, {
    badgeStyleOverrides: badgeStyleOverrides,
    children: /*#__PURE__*/_jsx(StyledSpan, Object.assign({
      $cssStyles: cssStyles
    }, domProps, {
      children: /*#__PURE__*/_jsx(Rendered, Object.assign({}, use !== 'dot' && (isBadge ? getBadgeStyles(use) : CountStyles), {
        children: /*#__PURE__*/_jsx(StyledTextWrapper, {
          $use: use,
          $isBadge: isBadge,
          children: children
        })
      }))
    }))
  });
};